import React, { useState, useMemo } from 'react';
import { Search, AlertCircle, Clock, ExternalLink, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useAdminData } from './AdminContext';
import { formatDateTime, getStatusLabel } from './utils';
import { formatLongDate } from '../../utils/dateFormatter';

const AdminInquiriesTab = () => {
  const { activeInquiries, overrideInquiryStatus, includeCompletedInquiries, setIncludeCompletedInquiries } = useAdminData();
  
  const [inquiriesSearch, setInquiriesSearch] = useState('');
  const [inquiriesStatusFilter, setInquiriesStatusFilter] = useState('all');
  const [overridingInquiry, setOverridingInquiry] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [expandedInquiry, setExpandedInquiry] = useState(null);

  const filteredInquiries = useMemo(() => {
    return activeInquiries.filter(inquiry => {
      const matchesSearch = inquiriesSearch === '' || 
        inquiry.customer_name?.toLowerCase().includes(inquiriesSearch.toLowerCase()) ||
        inquiry.vendor_name?.toLowerCase().includes(inquiriesSearch.toLowerCase()) ||
        inquiry.event_type?.toLowerCase().includes(inquiriesSearch.toLowerCase());
      const matchesStatus = inquiriesStatusFilter === 'all' || inquiry.status === inquiriesStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [activeInquiries, inquiriesSearch, inquiriesStatusFilter]);

  const getStatusBadgeClass = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800';
    if (status === 'accepted' || status === 'confirmed') return 'bg-emerald-100 text-emerald-800';
    if (status === 'proposal_sent') return 'bg-indigo-100 text-indigo-800';
    if (status === 'proposal_accepted') return 'bg-cyan-100 text-cyan-800';
    if (status === 'proposal_rejected') return 'bg-orange-100 text-orange-800';
    if (status === 'cancelled' || status === 'declined') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleOverrideStatus = async () => {
    if (!newStatus) return;
    await overrideInquiryStatus(overridingInquiry.id, newStatus);
    setOverridingInquiry(null);
    setNewStatus('');
  };

  const toggleExpand = (inquiryId) => {
    setExpandedInquiry(expandedInquiry === inquiryId ? null : inquiryId);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Inquiries</CardTitle>
          <CardDescription>Monitor and manage customer inquiries</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by customer, vendor, or event type..."
                value={inquiriesSearch}
                onChange={(e) => setInquiriesSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={inquiriesStatusFilter} onValueChange={setInquiriesStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                <SelectItem value="proposal_accepted">Proposal Accepted</SelectItem>
                <SelectItem value="proposal_rejected">Proposal Rejected</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                id="include-completed"
                checked={includeCompletedInquiries}
                onCheckedChange={setIncludeCompletedInquiries}
              />
              <Label htmlFor="include-completed" className="text-sm text-gray-600 cursor-pointer">
                Include completed
              </Label>
            </div>
          </div>

          {/* Inquiries Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      {inquiriesSearch ? 'No inquiries match your search' : 'No active inquiries'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInquiries.map((inquiry) => (
                    <React.Fragment key={inquiry.id}>
                      <TableRow className="hover:bg-gray-50">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpand(inquiry.id)}
                          >
                            {expandedInquiry === inquiry.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inquiry.customer_name}</p>
                            <p className="text-sm text-gray-500">{inquiry.customer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inquiry.vendor_name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{inquiry.business_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inquiry.event_type}</p>
                            <p className="text-sm text-gray-500">{inquiry.location}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inquiry.event_date ? formatLongDate(inquiry.event_date) : 'N/A'}</p>
                            <p className="text-xs text-gray-500">Created: {formatDateTime(inquiry.created_at)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(inquiry.status)}>
                            {getStatusLabel(inquiry.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">
                            {inquiry.agreed_price ? `$${inquiry.agreed_price}` : inquiry.budget || 'N/A'}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setOverridingInquiry(inquiry)}
                            >
                              <AlertCircle className="h-4 w-4 mr-1" />
                              Override
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded Details Row */}
                      {expandedInquiry === inquiry.id && (
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={8}>
                            <div className="p-4 space-y-3">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <Label className="text-xs text-gray-500">Guest Count</Label>
                                  <p>{inquiry.guest_count || 'N/A'}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">Budget</Label>
                                  <p>{inquiry.budget || 'N/A'}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">Inquiry ID</Label>
                                  <p className="font-mono text-xs">{inquiry.id}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">Last Updated</Label>
                                  <p className="text-sm">{formatDateTime(inquiry.updated_at)}</p>
                                </div>
                              </div>
                              {inquiry.message && (
                                <div>
                                  <Label className="text-xs text-gray-500">Customer Message</Label>
                                  <p className="text-sm bg-white p-2 rounded border mt-1">{inquiry.message}</p>
                                </div>
                              )}
                              {inquiry.vendor_note && (
                                <div>
                                  <Label className="text-xs text-gray-500">Vendor Note</Label>
                                  <p className="text-sm bg-white p-2 rounded border mt-1">{inquiry.vendor_note}</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Status Override Dialog */}
      {overridingInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Override Inquiry Status</CardTitle>
              <CardDescription>Admin override for inquiry status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Customer</Label>
                    <p className="font-medium">{overridingInquiry.customer_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Vendor</Label>
                    <p className="font-medium">{overridingInquiry.vendor_name}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Event</Label>
                  <p>{overridingInquiry.event_type} - {formatLongDate(overridingInquiry.event_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Current Status</Label>
                  <Badge className={getStatusBadgeClass(overridingInquiry.status)}>
                    {getStatusLabel(overridingInquiry.status)}
                  </Badge>
                </div>
              </div>

              <div>
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select new status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                    <SelectItem value="proposal_accepted">Proposal Accepted</SelectItem>
                    <SelectItem value="proposal_rejected">Proposal Rejected</SelectItem>
                    <SelectItem value="accepted">Accepted / Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-yellow-50 p-3 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This action will override the current inquiry status. 
                  Use this only for admin corrections.
                </p>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={handleOverrideStatus}
                  disabled={!newStatus}
                  className="flex-1"
                >
                  Update Status
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setOverridingInquiry(null); setNewStatus(''); }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default AdminInquiriesTab;
